/**
 * Miroir front de API/chalicelib/.../country_config (FR + BE + NL).
 * Source de vérité labels : i18n `registration:type.*.label` / `registration:country.*`.
 */

export type SupportedCountryCode = 'FR' | 'BE' | 'NL';

export type RegistrationTypeCode = 'siret' | 'bce' | 'kvk';

export interface CountryRegistrationType {
  type: RegistrationTypeCode;
  /** Clé i18n namespace registration */
  labelKey: string;
  placeholderKey: string;
  maxLength: number;
  /** Longueur attendue en chiffres purs (validation / textes SEPA). */
  digitLength: number;
}

export interface CountryFeatures {
  stripeConnect: boolean;
  billingUi: boolean;
  efacturation: boolean;
  /** Module pension / hébergement (nav + API). */
  boarding: boolean;
  boardingCerfa: boolean;
}

export interface ProCountryConfig {
  code: SupportedCountryCode;
  nameKey: string;
  flag: string;
  placesAutocompleteCountry: string;
  phoneRegion: string;
  /** Indicatif international affiché (ex. +33, +32). */
  dialCode: string;
  /** Fuseau IANA du marché (planning / dayjs.tz). */
  defaultTimezone: string;
  defaultRegistrationType: RegistrationTypeCode;
  registrationTypes: CountryRegistrationType[];
  features: CountryFeatures;
}

const FR: ProCountryConfig = {
  code: 'FR',
  nameKey: 'country.FR',
  flag: '🇫🇷',
  placesAutocompleteCountry: 'fr',
  phoneRegion: 'FR',
  dialCode: '+33',
  defaultTimezone: 'Europe/Paris',
  defaultRegistrationType: 'siret',
  registrationTypes: [
    {
      type: 'siret',
      labelKey: 'type.siret.label',
      placeholderKey: 'type.siret.placeholder',
      maxLength: 18,
      digitLength: 14,
    },
  ],
  features: {
    stripeConnect: true,
    billingUi: true,
    efacturation: true,
    boarding: true,
    boardingCerfa: true,
  },
};

const BE: ProCountryConfig = {
  code: 'BE',
  nameKey: 'country.BE',
  flag: '🇧🇪',
  placesAutocompleteCountry: 'be',
  phoneRegion: 'BE',
  dialCode: '+32',
  defaultTimezone: 'Europe/Brussels',
  defaultRegistrationType: 'bce',
  registrationTypes: [
    {
      type: 'bce',
      labelKey: 'type.bce.label',
      placeholderKey: 'type.bce.placeholder',
      maxLength: 12,
      digitLength: 10,
    },
  ],
  features: {
    stripeConnect: true,
    // Phase D : facturation native BE (devis / PDF). Peppol + pension restent FR-only.
    billingUi: true,
    efacturation: false,
    boarding: false,
    boardingCerfa: false,
  },
};

const NL: ProCountryConfig = {
  code: 'NL',
  nameKey: 'country.NL',
  flag: '🇳🇱',
  placesAutocompleteCountry: 'nl',
  phoneRegion: 'NL',
  dialCode: '+31',
  defaultTimezone: 'Europe/Amsterdam',
  defaultRegistrationType: 'kvk',
  registrationTypes: [
    {
      type: 'kvk',
      labelKey: 'type.kvk.label',
      placeholderKey: 'type.kvk.placeholder',
      maxLength: 9,
      digitLength: 8,
    },
  ],
  features: {
    stripeConnect: true,
    // Facturation native amorçable (KOR). Peppol + pension restent FR-only.
    billingUi: true,
    efacturation: false,
    boarding: false,
    boardingCerfa: false,
  },
};

const REGISTRY: Record<SupportedCountryCode, ProCountryConfig> = {
  FR,
  BE,
  NL,
};

export const SUPPORTED_COUNTRIES: SupportedCountryCode[] = ['FR', 'BE', 'NL'];

export function normalizeCountryCode(code?: string | null): SupportedCountryCode {
  const key = (code || 'FR').trim().toUpperCase().slice(0, 2);
  if (key === 'BE') return 'BE';
  if (key === 'NL') return 'NL';
  return 'FR';
}

export function getCountryConfig(code?: string | null): ProCountryConfig {
  return REGISTRY[normalizeCountryCode(code)];
}

export function getPlacesCountryRestriction(proCountry?: string | null): string {
  return getCountryConfig(proCountry).placesAutocompleteCountry;
}

export function getDefaultRegistrationType(code?: string | null): RegistrationTypeCode {
  return getCountryConfig(code).defaultRegistrationType;
}

export function getRegistrationTypeConfig(
  country?: string | null,
  registrationType?: string | null
): CountryRegistrationType {
  const cfg = getCountryConfig(country);
  const type = (registrationType || cfg.defaultRegistrationType).toLowerCase();
  return (
    cfg.registrationTypes.find((rt) => rt.type === type) || cfg.registrationTypes[0]
  );
}

export function getCountryFeatures(code?: string | null): CountryFeatures {
  return getCountryConfig(code).features;
}

export function getDefaultTimezone(code?: string | null): string {
  return getCountryConfig(code).defaultTimezone;
}
